import {
  KalturaAssetType,
  KalturaPurchaseStatus,
  KalturaRecordingType,
  KalturaSubscriptionDependencyType,
  KalturaMediaFile,
} from '@kaltura-ott/ott-client-types/api/types';

import type { MediaType } from '../lib/dms';
import type { KuxImages } from '../lib/utils/image';
import type { KuxCategoryMetas, KuxMultiTags } from '../lib/utils/meta';

import { KuxMenuItem } from './menu';
import { FormatKalturaProductPricePpv, KuxPrice } from './subscriptions';

export interface KuxMediaAssetType {
  id: number;
  images: KuxImages;
  title: string;
  adult: boolean;
  parentalRating: number;
  type: number;
  metas: KuxCategoryMetas;
  tags: KuxMultiTags;
  mediaType: MediaType;
  mediaFilesTypes?: string[];
  mediaFiles?: KalturaMediaFile[];
}
export interface KuxLinkAsAssetType extends KuxMediaAssetType {
  linkItem?: KuxMenuItem;
}

export enum LinearChannelType {
  mosaic = 'mosaic',
  video = 'video',
  hidden = 'hidden',
}

export interface KuxLinearType extends KuxMediaAssetType {
  catchUpBuffer: number;
  channelNumber: number;
  description: string;
  enableCatchUp: boolean;
  enableCdvr: boolean;
  enableStartOver: boolean;
  enableTrickPlay: boolean;
  mediaFileId?: number;
  representPackage?: boolean;
  mediaType: MediaType;
  purchaseStatus?: KalturaPurchaseStatus;
  price?: FormatKalturaProductPricePpv;
  isPlaceholder?: boolean;
  linearChannelType?: LinearChannelType | undefined;
  blockFastForward?: boolean;
  externalId: string;
  mosaicSupport?: boolean;
}

export interface KuxProgramPlaceholderType {
  id: number;
  endDate: number;
  startDate: number;
  linearAssetId: number;
  title?: string;
  duration?: number;
}

export interface KuxCardPlaceholderType {
  assetId: number;
  assetType: KalturaAssetType;
  isRecordProgram?: boolean;
  title?: string;
}

export interface MosaicChannelsInfo {
  order: number;
  audioLang: string;
  channelExternalId: string;
  title: string;
  programExternalId: string;
}

export interface MosaicInfoTag {
  mosaicProgramExternalId?: string;
  sourceProgramExternalId?: string;
  mosaicChannelsInfo?: MosaicChannelsInfo[];
  type?: LinearChannelType;
}

export interface KuxProgramType extends KuxMediaAssetType {
  description: string;
  duration: number;
  enableCatchUp: boolean;
  enableCdvr: boolean;
  enableStartOver: boolean;
  enableTrickPlay: boolean;
  endDate: number;
  epgId: string;
  episodeName?: string;
  episodeNumber?: number;
  linearAssetId: number;
  mediaType: MediaType;
  metas: KuxCategoryMetas;
  // TODO deprecated
  notFound?: boolean;
  isNotFound?: boolean;
  isPopular: boolean;
  recordable: boolean;
  seasonNumber?: number;
  seriesId?: string;
  startDate: number;
  tags: KuxMultiTags;
  newEpisodeBadgeDays?: number;
  recordingExpirationBoundary?: number;
  isRecordProgram?: boolean;
  recordData?: {
    recordingId: number;
    recordingType?: KalturaRecordingType;
    // only for series
    totalRecordedEpisodes?: number;
    // only for singles
    viewableUntilDate?: number;
  };
  externalId: string;
  permanentProgram?: KuxProgramType;
  mosaicInfo?: {
    isMosaicEvent: '1' | '0';
    mosaicProgramExternalId?: string;
    sourceProgramExternalId?: string;
    sourceLinearAssetId?: number;
    mosaicChannelsInfo?: MosaicChannelsInfo[];
    type?: 'hidden' | 'mosaic' | 'video';
  };
  isGroupedSeries: boolean;
}

export interface KuxMovieType extends KuxMediaAssetType {
  description: string;
  mediaFileId?: number;
  tags: KuxMultiTags;
  mediaType: MediaType;
  duration: number;
}

export interface KuxEpisodeType extends KuxMovieType {
  seriesId?: string;
  episodeNumber?: number;
  seasonNumber?: number;
  episodeName?: string;
  isGroupedSeries: boolean;
}

export interface KuxSeriesType extends KuxMediaAssetType {
  seriesId?: string;
  description: string;
  mediaFileId?: number;
  tags: KuxMultiTags;
  mediaType: MediaType;
}

export interface KuxPpv {
  id: string;
  viewLifeCycle: number;
  price: KuxPrice;
  fullPrice: KuxPrice;
  isSubscriptionOnly: boolean;
}

export interface KuxSubscription {
  id: string;
  dependencyType: KalturaSubscriptionDependencyType | string;
  title: string;
  externalId: string;
  description: string;
  price: KuxPrice;
  provider?: string;
}

export interface KuxPackage extends KuxMediaAssetType {
  id: number;
  startDate: number;
  endDate: number;
  title: string;
  description: string;
  image: string;
  images: KuxImages;
  priceDescription: string;
  disclaimer: string;
  subscriptionId: number;
  basePackageId: number;
  catalogStartDateTime: string;
  playbackEndDateTime: string;
  tags: KuxMultiTags;
  mediaType: MediaType;
  externalId: string;
}

export interface KuxPurchaseInfo {
  purchaseStatus?: KalturaPurchaseStatus;
  purchaseDate?: number;
  isPurchasePending?: boolean;
}

export interface SubscriptionCollection {
  subscription: {
    id: string;
    price: KuxPrice;
    title: string;
    dependencyType: string;
    externalId: string;
    description: string;
  };
  assetPackage: KuxPackage;
  purchaseInfo: {
    purchaseStatus: KalturaPurchaseStatus;
  };
}

export interface KuxPerson extends KuxMediaAssetType {
  description: string;
}

export interface KuxLogo extends KuxMediaAssetType {
  description: string;
}
